import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useRef } from 'react';
import { Button, Card, Column, Row, TextButton, Typography, CloseButton } from 'seagull';
import { LogoPicker } from '~/components/LogoPicker/LogoPicker';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useScrollBorder } from '~/hooks/useScrollBorder';
import { useBrandingActions } from '../BrandingActionsContext';
import { PreviewState } from '../ClientOnboarding.utils';
import { useCobBranding } from '../CobBrandingContext';
import { useCobConfig } from '../CobConfigContext';
import styles from './BrandingControllers.module.scss';
import { ColorsLabel, SectionTitle } from './BrandingControllers.shared';
import { ColorSelectorWithPopup } from './ColorSelectorWithPopup';
import { PrimaryBackgroundColorPopup } from './PrimaryBackgroundColorPopup';
/** In V2, progress bar uses brand color; sync progressBarComplete to buttonPrimary on every branding update. */ function syncProgressBarToBrandColor(prev, next) {
    return _object_spread_props(_object_spread({}, next), {
        progressBarComplete: next.buttonPrimary
    });
}
export function BrandingPanelV2() {
    const { branding, logoImage, errors, isDirty, isValid, setBranding: setBrandingContext, setLogo, revertToDefault, saveLogoAndBranding } = useCobBranding();
    const setBranding = useCallback((update)=>{
        setBrandingContext((prev)=>{
            const next = typeof update === 'function' ? update(prev) : update;
            return syncProgressBarToBrandColor(prev, next);
        });
    }, [
        setBrandingContext
    ]);
    const { SET_UP_BRAND_CLIENT_PREVIEW = false } = useFeatureFlags();
    const { originalPreviewState } = useCobConfig();
    const { onToggleBrandSetup } = useBrandingActions();
    function handleCloseClick() {
        onToggleBrandSetup();
    }
    const contentRef = useRef(null);
    const { headerClassName, footerClassName } = useScrollBorder({
        contentRef,
        headerBaseClassName: styles.headerSection,
        footerBaseClassName: styles.saveSection
    }, []);
    return /*#__PURE__*/ _jsxs(Card, {
        className: styles.brandingCardNew,
        children: [
            SET_UP_BRAND_CLIENT_PREVIEW ? /*#__PURE__*/ _jsxs(Row, {
                alignItems: "center",
                justifyContent: "space-between",
                className: headerClassName,
                children: [
                    /*#__PURE__*/ _jsx(SectionTitle, {
                        variant: "paragraph1",
                        children: 'Customize brand'
                    }),
                    originalPreviewState !== PreviewState.SetupBrand && /*#__PURE__*/ _jsx(CloseButton, {
                        skin: "standard",
                        onClick: handleCloseClick,
                        testId: "close-branding-setup-btn"
                    })
                ]
            }) : /*#__PURE__*/ _jsx("div", {
                className: headerClassName,
                children: /*#__PURE__*/ _jsx(SectionTitle, {
                    children: 'Upload logo'
                })
            }),
            /*#__PURE__*/ _jsxs("div", {
                ref: contentRef,
                className: styles.scrollableContent,
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: styles.logoSection,
                        children: /*#__PURE__*/ _jsx(Column, {
                            gap: 12,
                            children: /*#__PURE__*/ _jsx(LogoPicker, {
                                isNewUI: true,
                                testId: "upload-logo",
                                image: logoImage,
                                onChange: setLogo
                            })
                        })
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: styles.colorsSection,
                        children: /*#__PURE__*/ _jsxs(Column, {
                            gap: 24,
                            children: [
                                /*#__PURE__*/ _jsxs(Row, {
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    children: [
                                        /*#__PURE__*/ _jsx(ColorsLabel, {
                                            children: "Brand color"
                                        }),
                                        /*#__PURE__*/ _jsx(Row, {
                                            gap: 6,
                                            children: /*#__PURE__*/ _jsx(ColorSelectorWithPopup, {
                                                branding: branding,
                                                hasError: errors.has('buttonPrimary'),
                                                colorKey: "buttonPrimary",
                                                onChange: setBranding
                                            })
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs(Row, {
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    children: [
                                        /*#__PURE__*/ _jsx(ColorsLabel, {
                                            children: "Primary background"
                                        }),
                                        /*#__PURE__*/ _jsx(Row, {
                                            gap: 6,
                                            children: /*#__PURE__*/ _jsx(PrimaryBackgroundColorPopup, {
                                                branding: branding,
                                                hasError: errors.has('backgroundGradientStart') || errors.has('backgroundGradientEnd'),
                                                onChange: setBranding
                                            })
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs(Row, {
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    children: [
                                        /*#__PURE__*/ _jsx(ColorsLabel, {
                                            children: "Secondary background"
                                        }),
                                        /*#__PURE__*/ _jsx(Row, {
                                            gap: 6,
                                            children: /*#__PURE__*/ _jsx(ColorSelectorWithPopup, {
                                                branding: branding,
                                                hasError: errors.has('backgroundStandard'),
                                                colorKey: "backgroundStandard",
                                                onChange: setBranding
                                            })
                                        })
                                    ]
                                })
                            ]
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(Column, {
                gap: 12,
                alignItems: "center",
                className: footerClassName,
                children: [
                    /*#__PURE__*/ _jsx(Button, {
                        skin: "primaryOutline",
                        disabled: !isDirty || !isValid,
                        testId: "save-colors",
                        tooltip: {
                            placement: 'top',
                            title: !isDirty ? 'Nothing to save' : !isValid ? 'One of the fields is invalid' : ''
                        },
                        onClick: saveLogoAndBranding,
                        className: styles.saveBtn,
                        children: "Save changes"
                    }),
                    /*#__PURE__*/ _jsx(TextButton, {
                        testId: "undo-color-changes",
                        variant: "paragraph2",
                        skin: "primary",
                        underline: "none",
                        weight: "normal",
                        onClick: revertToDefault,
                        children: "Revert to default colors"
                    }),
                    /*#__PURE__*/ _jsx(Typography, {
                        variant: "paragraph3",
                        skin: "secondary",
                        className: styles.disclaimer,
                        children: "Changes saved here will apply to all of your proposals."
                    })
                ]
            })
        ]
    });
}
